import { logger } from "@/lib/logger";
/**
 * Generic Offline Data Store using IndexedDB
 * Provides offline-first data caching for any entity type
 * 
 * CRASH-SAFE: All methods handle IndexedDB unavailability gracefully
 */

import { isIndexedDBAvailable, safeDispatchEvent } from '@/lib/browserSafeUtils';

export interface CachedData<T = any> {
  id: string;
  key: string; // Cache key (e.g., "trials", "enrollments:patient-123")
  data: T;
  cachedAt: number;
  expiresAt: number | null; // null = never expires
  syncedAt: number | null; // Last time synced with server
  version: number; // For conflict detection
}

export interface SyncMetadata {
  key: string;
  lastSyncedAt: number;
  syncCount: number;
  lastError: string | null;
}

const DB_NAME = 'bridge-offline-data';
const DB_VERSION = 1;

const STORES = {
  DATA: 'cached-data',
  SYNC_META: 'sync-metadata',
} as const;

class OfflineDataStore {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private initFailed: boolean = false;

  async init(): Promise<void> {
    if (this.initFailed) return;
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    if (!isIndexedDBAvailable()) {
      logger.warn('[offlineDataStore] IndexedDB not available');
      this.initFailed = true;
      return;
    }

    this.initPromise = new Promise<void>((resolve) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
          logger.warn('[offlineDataStore] Failed to open IndexedDB:', request.error);
          this.initFailed = true;
          this.initPromise = null;
          resolve();
        };

        request.onsuccess = () => {
          this.db = request.result;
          this.initPromise = null;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          try {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(STORES.DATA)) {
              const dataStore = db.createObjectStore(STORES.DATA, { keyPath: 'id' });
              dataStore.createIndex('key', 'key', { unique: false });
              dataStore.createIndex('expiresAt', 'expiresAt', { unique: false });
            }

            if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
              db.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
            }
          } catch (error) {
            logger.warn('[offlineDataStore] Failed to upgrade database:', error);
          }
        };

        request.onblocked = () => {
          logger.warn('[offlineDataStore] Database blocked');
          this.initFailed = true;
          this.initPromise = null;
          resolve();
        };
      } catch (error) {
        logger.warn('[offlineDataStore] Exception during init:', error);
        this.initFailed = true;
        this.initPromise = null;
        resolve();
      }
    });

    return this.initPromise;
  }

  isAvailable(): boolean {
    return this.db !== null && !this.initFailed;
  }

  /**
   * Cache data with a specific key
   */
  async set<T>(key: string, data: T, ttlSeconds?: number): Promise<boolean> {
    try {
      await this.init();
      if (!this.db) return false;

      const now = Date.now();
      const cached: CachedData<T> = {
        id: key, // Use key as ID for atomic upsert
        key,
        data,
        cachedAt: now,
        expiresAt: ttlSeconds ? now + (ttlSeconds * 1000) : null,
        syncedAt: now,
        version: 1,
      };

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readwrite');
          const store = transaction.objectStore(STORES.DATA);
          // Use put() for atomic upsert — prevents data loss if crash occurs
          // between delete and add (was previously deleteByKey + add)
          const request = store.put(cached);

          request.onsuccess = () => {
            safeDispatchEvent('offline-data-updated', { key, action: 'set' });
            resolve(true);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to set:', request.error);
            resolve(false);
          };
          // Storage pressure / private mode can abort without a request error
          transaction.onabort = () => {
            logger.warn('[offlineDataStore] set aborted:', transaction.error);
            resolve(false);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception setting data:', error);
          resolve(false);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] set error:', error);
      return false;
    }
  }

  /**
   * Bulk upsert many entries in ONE transaction (one event per call).
   * Use for large offline preparation runs — avoids thousands of
   * transactions/events that freeze tablet browsers.
   */
  async setMany<T>(entries: Array<{ key: string; data: T }>, ttlSeconds?: number): Promise<number> {
    if (entries.length === 0) return 0;
    try {
      await this.init();
      if (!this.db) return 0;
      const now = Date.now();
      return await new Promise<number>((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readwrite');
          const store = transaction.objectStore(STORES.DATA);
          for (const { key, data } of entries) {
            store.put({
              id: key,
              key,
              data,
              cachedAt: now,
              expiresAt: ttlSeconds ? now + ttlSeconds * 1000 : null,
              syncedAt: now,
              version: 1,
            } as CachedData<T>);
          }
          transaction.oncomplete = () => {
            safeDispatchEvent('offline-data-updated', { key: 'bulk', action: 'set', count: entries.length });
            resolve(entries.length);
          };
          transaction.onerror = () => {
            logger.warn('[offlineDataStore] setMany failed:', transaction.error);
            resolve(0);
          };
          transaction.onabort = () => {
            logger.warn('[offlineDataStore] setMany aborted:', transaction.error);
            resolve(0);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception in setMany:', error);
          resolve(0);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] setMany error:', error);
      return 0;
    }
  }

  /**
   * Get cached data by key
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      await this.init();
      if (!this.db) return null;

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readonly');
          const store = transaction.objectStore(STORES.DATA);
          const index = store.index('key');
          const request = index.getAll(key);

          request.onsuccess = () => {
            const results = request.result as CachedData<T>[];
            if (results.length === 0) {
              resolve(null);
              return;
            }

            // Get the most recent entry
            const latest = results.sort((a, b) => b.cachedAt - a.cachedAt)[0];

            // Check if expired
            if (latest.expiresAt && latest.expiresAt < Date.now()) {
              // Data is expired, clean it up
              this.deleteByKey(key).catch((e) => logger.warn(e));
              resolve(null);
              return;
            }

            resolve(latest.data);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to get:', request.error);
            resolve(null);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception getting data:', error);
          resolve(null);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] get error:', error);
      return null;
    }
  }

  /**
   * Get cached data with metadata
   */
  async getWithMeta<T>(key: string): Promise<CachedData<T> | null> {
    try {
      await this.init();
      if (!this.db) return null;

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readonly');
          const store = transaction.objectStore(STORES.DATA);
          const index = store.index('key');
          const request = index.getAll(key);

          request.onsuccess = () => {
            const results = request.result as CachedData<T>[];
            if (results.length === 0) {
              resolve(null);
              return;
            }

            const latest = results.sort((a, b) => b.cachedAt - a.cachedAt)[0];

            if (latest.expiresAt && latest.expiresAt < Date.now()) {
              this.deleteByKey(key).catch((e) => logger.warn(e));
              resolve(null);
              return;
            }

            resolve(latest);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to getWithMeta:', request.error);
            resolve(null);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception in getWithMeta:', error);
          resolve(null);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] getWithMeta error:', error);
      return null;
    }
  }

  /**
   * Delete all entries for a key
   */
  async deleteByKey(key: string): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readwrite');
          const store = transaction.objectStore(STORES.DATA);
          const index = store.index('key');
          const request = index.getAllKeys(key);

          request.onsuccess = () => {
            const keys = request.result;
            keys.forEach((k) => store.delete(k));
            safeDispatchEvent('offline-data-updated', { key, action: 'delete' });
            resolve();
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to delete:', request.error);
            resolve();
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception deleting data:', error);
          resolve();
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] deleteByKey error:', error);
    }
  }

  /**
   * Get all cache keys
   */
  async getAllKeys(): Promise<string[]> {
    try {
      await this.init();
      if (!this.db) return [];

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readonly');
          const store = transaction.objectStore(STORES.DATA);
          const request = store.getAll();

          request.onsuccess = () => {
            const results = request.result as CachedData[];
            const uniqueKeys = [...new Set(results.map(r => r.key))];
            resolve(uniqueKeys);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to get keys:', request.error);
            resolve([]);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception getting keys:', error);
          resolve([]);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] getAllKeys error:', error);
      return [];
    }
  }

  /**
   * Clear all expired data
   */
  async clearExpired(): Promise<number> {
    try {
      await this.init();
      if (!this.db) return 0;

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readwrite');
          const store = transaction.objectStore(STORES.DATA);
          const request = store.getAll();

          request.onsuccess = () => {
            const results = request.result as CachedData[];
            const now = Date.now();
            let cleared = 0;

            results.forEach((item) => {
              if (item.expiresAt && item.expiresAt < now) {
                store.delete(item.id);
                cleared++;
              }
            });

            resolve(cleared);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to clear expired:', request.error);
            resolve(0);
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception clearing expired:', error);
          resolve(0);
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] clearExpired error:', error);
      return 0;
    }
  }

  /**
   * Clear all cached data
   */
  async clearAll(): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readwrite');
          const store = transaction.objectStore(STORES.DATA);
          const request = store.clear();

          request.onsuccess = () => {
            safeDispatchEvent('offline-data-updated', { action: 'clear-all' });
            resolve();
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to clear all:', request.error);
            resolve();
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception clearing all:', error);
          resolve();
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] clearAll error:', error);
    }
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<{
    totalItems: number;
    totalKeys: number;
    oldestItem: number | null;
    newestItem: number | null;
    expiredCount: number;
  }> {
    try {
      await this.init();
      if (!this.db) {
        return { totalItems: 0, totalKeys: 0, oldestItem: null, newestItem: null, expiredCount: 0 };
      }

      return new Promise((resolve) => {
        try {
          const transaction = this.db!.transaction([STORES.DATA], 'readonly');
          const store = transaction.objectStore(STORES.DATA);
          const request = store.getAll();

          request.onsuccess = () => {
            const results = request.result as CachedData[];
            const now = Date.now();

            const stats = {
              totalItems: results.length,
              totalKeys: new Set(results.map(r => r.key)).size,
              oldestItem: results.length > 0 ? Math.min(...results.map(r => r.cachedAt)) : null,
              newestItem: results.length > 0 ? Math.max(...results.map(r => r.cachedAt)) : null,
              expiredCount: results.filter(r => r.expiresAt && r.expiresAt < now).length,
            };

            resolve(stats);
          };
          request.onerror = () => {
            logger.warn('[offlineDataStore] Failed to get stats:', request.error);
            resolve({ totalItems: 0, totalKeys: 0, oldestItem: null, newestItem: null, expiredCount: 0 });
          };
        } catch (error) {
          logger.warn('[offlineDataStore] Exception getting stats:', error);
          resolve({ totalItems: 0, totalKeys: 0, oldestItem: null, newestItem: null, expiredCount: 0 });
        }
      });
    } catch (error) {
      logger.warn('[offlineDataStore] getStats error:', error);
      return { totalItems: 0, totalKeys: 0, oldestItem: null, newestItem: null, expiredCount: 0 };
    }
  }
}

// Singleton instance
let _instance: OfflineDataStore | null = null;

function getInstance(): OfflineDataStore {
  if (!_instance) {
    _instance = new OfflineDataStore();
  }
  return _instance;
}

// Export proxy for safe access
export const offlineDataStore = {
  init: () => getInstance().init(),
  isAvailable: () => getInstance().isAvailable(),
  set: <T>(key: string, data: T, ttlSeconds?: number) => getInstance().set(key, data, ttlSeconds),
  setMany: <T>(entries: Array<{ key: string; data: T }>, ttlSeconds?: number) => getInstance().setMany<T>(entries, ttlSeconds),
  get: <T>(key: string) => getInstance().get<T>(key),
  getWithMeta: <T>(key: string) => getInstance().getWithMeta<T>(key),
  deleteByKey: (key: string) => getInstance().deleteByKey(key),
  getAllKeys: () => getInstance().getAllKeys(),
  clearExpired: () => getInstance().clearExpired(),
  clearAll: () => getInstance().clearAll(),
  getStats: () => getInstance().getStats(),
};
